<script setup lang="ts">
import type { Space } from "@mmh/types"
import { InquiryType } from "@mmh/types/constants"
import { UsersRound } from "lucide-vue-next"
import type { HTMLAttributes } from "vue"
import { useRoute, useRouter } from "vue-router"
import { InquiryDialogV2 } from "~/components/inquiry"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { Badge } from "~/components/ui/badge"
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious, type CarouselApi } from "~/components/ui/carousel"
import { Separator } from "~/components/ui/separator"
import { useLocation } from "~/composables/locations"
import { scrollToAndFocus } from "~/composables/scroll"
import { cn } from "~/lib/utils"

const { class: cl } = defineProps<{
    class?: HTMLAttributes["class"]
}>()

const { location, menuPath } = useLocation()
const router = useRouter()
const route = useRoute()

const mainApi = ref<CarouselApi>()
const currentIndex = ref(0)
const spaces = computed(() => location.value?.spaces)
const selectedSpace = computed(() => spaces.value?.[currentIndex.value])
const hasSpacesImages = computed(() => spaces.value?.some((space: Space) => space.src))

function onThumbnailClick(index: number | string) {
    mainApi.value?.scrollTo(Number(index))
}

async function handleScrollToMenu(event: Event) {
    event.preventDefault()
    
    await router.push(menuPath("large-party"))
    
    scrollToAndFocus("menus")
}

onMounted(() => {
    if (!mainApi.value) return

    currentIndex.value = mainApi.value.selectedScrollSnap() || 0
    
    mainApi.value.on("select", () => {
        const newIndex = mainApi.value?.selectedScrollSnap() || 0
        currentIndex.value = newIndex
    })
})
</script>

<template>
    <div
        v-if="location"
        id="private-dining"
        tabindex="-1"
        :class="cn('space-y-10 md:space-y-12 px-4 sm:px-6', cl)"
    >
        <header class="space-y-6 text-center">
            <h2 class="text-2xl sm:text-3xl lg:text-4xl font-headline text-balance max-w-full overflow-hidden">
                Private Dining
            </h2>
        </header>
        
        <div 
            v-if="spaces && spaces.length && hasSpacesImages"
            class="flex flex-col lg:flex-row gap-6 lg:gap-8"
        >
            <div class="w-full lg:w-[60%] min-w-0">
                <div class="flex flex-col md:gap-2">
                    <Carousel
                        tabindex="-1"
                        @init-api="mainApi = $event"
                    >
                        <CarouselContent>
                            <CarouselItem
                                v-for="space in spaces"
                                :key="space.title"
                            >
                                <ThemeImg 
                                    v-if="space.src"
                                    :src="space.src" 
                                    :alt="space.description"
                                    class="w-full object-cover aspect-square md:aspect-auto md:h-[550px]"
                                />
                            </CarouselItem>
                        </CarouselContent>
                        <CarouselPrevious
                            v-if="spaces && spaces.length > 1"
                            class="size-8 left-4 hidden md:flex rounded border-none bg-foreground text-background"
                        />
                        <CarouselNext
                            v-if="spaces && spaces.length > 1"
                            class="size-8 right-4 hidden md:flex rounded border-none bg-foreground text-background"
                        />
                        <div class="absolute bottom-0 w-full p-4 bg-background/50 backdrop-blur-sm text-foreground grid place-items-center gap-4">
                            <div class="flex flex-col items-center gap-2 font-headline">
                                <span class="text-sm">{{ selectedSpace?.title }}</span>
                                <Badge
                                    v-if="selectedSpace?.minGuests && selectedSpace?.maxGuests"
                                    class="w-fit flex items-center gap-2 font-button rounded bg-background/80 text-foreground backdrop-blur-sm hover:bg-background/80"
                                >
                                    <UsersRound class="size-3" />
                                    <span class="capsize">{{ selectedSpace?.minGuests }} - {{ selectedSpace?.maxGuests }} Guests</span>
                                </Badge>
                            </div>
                        </div>
                    </Carousel>
                    <div
                        v-if="spaces && spaces.length > 1"
                        class="flex gap-3 justify-center pt-2"
                    >
                        <button
                            v-for="(space, index) in spaces"
                            :key="space.title"
                            :class="cn(
                                'relative w-20 aspect-square overflow-hidden rounded-sm transition-opacity duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground focus-visible:ring-offset-2',
                                currentIndex === index 
                                    ? 'opacity-100 ring-2 ring-foreground ring-offset-1' 
                                    : 'opacity-60 hover:opacity-90'
                            )"
                            tabindex="0"
                            :aria-label="`View ${space.title} space`"
                            :aria-current="currentIndex === index ? 'true' : undefined"
                            @click="onThumbnailClick(index)"
                            @keydown.enter="onThumbnailClick(index)"
                            @keydown.space.prevent="onThumbnailClick(index)"
                        >
                            <ThemeImg
                                v-if="space.src"
                                :src="space.src"
                                :alt="space.alt|| ''"
                                class="object-cover w-full h-full"
                            />
                        </button>
                    </div>
                </div>
            </div>
            <div class="w-full lg:w-[40%] text-center min-w-0">
                <div class="flex flex-col gap-6 justify-around py-6 lg:p-8 bg-foreground-faded border-l-8 border-background/50 min-w-0">
                    <div class="space-y-6">
                        <header class="space-y-4 text-center text-balance">
                            <h3 class="text-xl font-headline">
                                Enjoy Your Own Space
                            </h3>
                            <Separator class="mx-auto w-10 bg-foreground/20" />
                        </header>
                        <div class="space-y-4 max-w-lg mx-auto text-center">
                            <p
                                class="text-balance break-words max-w-full"
                                v-html="location?.pageContent?.privateDining?.content || ''"
                            />
                        </div>
                    </div>
                    <div class="flex flex-col items-stretch sm:items-center justify-center gap-4 mx-auto">
                        <ThemeButton 
                            size="lg"
                            variant="outline"
                            class="border-foreground/20 w-full sm:w-auto text-balance"
                            tabindex="0"
                            aria-label="View Prix Fixe Menus"
                            @click="handleScrollToMenu"
                        >
                            View Prix Fixe Menus
                        </ThemeButton>
                        <InquiryDialogV2
                            :type="InquiryType.PrivateDining"
                            :location="location"
                            :to="{ path: route.path, query: { modal: 'private-dining-inquiry-form' } }"
                            trigger-type="private_dining_section"
                        >
                            <ThemeButton 
                                size="lg"
                                class="w-full sm:w-auto text-balance"
                                aria-label="Private Events Inquiry"
                                tabindex="0"
                            >
                                Inquire Now
                            </ThemeButton>
                        </InquiryDialogV2>
                    </div>
                </div>
            </div>
        </div>
        
        <div 
            v-else
            class="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8"
        >
            <ThemeImg 
                :src="(location?.pageContent?.privateDining as any)?.image || ''" 
                :alt="location?.pageContent?.privateDining?.imageAlt || ''"
                class="w-full aspect-[4/3] md:h-full md:aspect-auto object-cover"
            />
            <div class="flex flex-col gap-6 justify-around py-6 lg:p-8 bg-foreground-faded border-l-8 border-background/50 min-w-0">
                <div class="space-y-6">
                    <header class="space-y-4 text-center text-balance">
                        <h3 class="text-xl font-headline">
                            Enjoy Your Own Space
                        </h3>
                        <Separator class="mx-auto w-10 bg-foreground/20" />
                    </header>
                    <div class="space-y-4 max-w-lg mx-auto text-center">
                        <p
                            class="text-balance break-words max-w-full"
                            v-html="location?.pageContent?.privateDining?.content || ''"
                        />
                    </div>
                </div>
                <div class="flex flex-col items-stretch sm:items-center justify-center gap-4 mx-auto">
                    <ThemeButton 
                        size="lg"
                        variant="outline"
                        class="border-foreground/20 w-full sm:w-auto text-balance"
                        tabindex="0"
                        aria-label="View Prix Fixe Menus"
                        @click="handleScrollToMenu"
                    >
                        View Prix Fixe Menus
                    </ThemeButton>
                    <InquiryDialogV2
                        :type="InquiryType.PrivateDining"
                        :location="location"
                        :to="{ path: route.path, query: { modal: 'private-dining-inquiry-form' } }"
                        trigger-type="private_dining_section"
                    >
                        <ThemeButton 
                            size="lg"
                            class="w-full sm:w-auto text-balance"
                            aria-label="Private Events Inquiry"
                            tabindex="0"
                        >
                            Inquire Now
                        </ThemeButton>
                    </InquiryDialogV2>
                </div>
            </div>
        </div>
    </div>
</template>